import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Icon } from "@vds/icons";
import "../AddressModal.css";
import { CommonValidateAddress } from "../CommonValidateAddress";
import { ValidateInstallationAddress } from "../ValidateInstallationAddress";
import styled from "styled-components";
import { Input } from "@vds/inputs";
import { EMAIL_REGEX_C360 } from "../../../constants/ValidationConstants";

export const CustomInput = styled(Input)`
  padding-bottom: 0.5rem;
  height: auto;
  width: 100%;
  max-width: 100%;
  & * {
    max-width: 100%;
  }
  & label {
    & p {
      font-size: 12px;
      font-weight: ${(props) => (props.fontWeight ? props.fontWeight : "400")};
      color: ${(props) => (props.disabled ? "#d8dada" : "")};
    }
  }
  & span {
    & span {
      & span {
        width: ${(props) => (props.boxWidth ? props.boxWidth : "")};
      }
    }
  }
  & input {
    font-size: 12px;
    height: ${(props) => (props.height ? props.height : "")};
    &:disabled {
      -webkit-text-fill-color: #000000;
      background-color: #ffffff;
      border-bottom: 1px solid #000000;
      border-top: 1px solid #dfe0e0;
      color: ${(props) => (props.disabled ? "#d8dada" : "")};
      opacity: ${(props) => (props.disabled ? "0.4" : "")};
    }
  }
  & span {
    width: ${(props) => (props.width ? props.width : "")};
    & span {
      & span {
        & div:not(:first-child) {
          & svg {
            display: none; // hide success tick icon
          }
        }
      }
    }
  }
`;

//  Validation regexes
const PHONE_REGEX = /^\+?[1-9]\d{9,14}$/; // 10-15 digits, optional leading +
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // standard email format
const INITIAL_ADDRESS_STATE = {
  verified: false,
  addressByPass: null,
  locationFound: null,
  timezone: "",
  latitude: "",
  longitude: "",
  locusSuggestedAddress: "",
  historicalAddress: { value: "", error: false, errorText: "", success: false },
  address1: { value: "", error: false, errorText: "Enter valid street address", success: false },
  address2: { value: "", error: false, errorText: "Enter address 2", success: false },
  city: { value: "", error: false, errorText: "Enter city", success: false },
  country: { value: null, options: [], error: false, errorText: "Please select country", success: false },
  state: { value: null, options: [], allStateOptions: null, error: false, errorText: "Please select state", success: false },
  zip: { value: "", error: false, errorText: "Enter zip code", success: false },
};

const AddressModal = ({ isOpen, onSave, onCancel, label, typeofModal }) => {
  const [isLoading, setLoader] = useState(false);
  const [address, setAddress] = useState(INITIAL_ADDRESS_STATE);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [newName, setNewName] = useState({
    value: "",
    error: false,
    errorText: "Enter name",
    success: false,
  });
  const [newPhone, setNewPhone] = useState({
    value: "",
    error: false,
    errorText: "Enter phone",
    success: false,
  });

  const [newEmail, setNewEmail] = useState({
    value: "",
    error: false,
    errorText: "Enter email",
    success: false,
  });

  useEffect(() => {
    const disabled = !newName.value?.trim() || !newPhone.value?.trim() || !newEmail.value?.trim() || !address?.verified; // ✅ Now this will update when address.verified changes

    setIsSaveDisabled(disabled);
  }, [
    newName.value,
    newPhone.value,
    newEmail.value,
    address?.verified, // ✅ Add address.verified as dependency
  ]);

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length === 0) return "";
    if (digits.length > 10) return digits;
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handleFields = (field, value, isAutoPopulated = false) => {
    switch (field) {
      case "newName": {
        if (value && value.length) {
          setNewName({ value, error: false, errorText: "Enter name", success: true });
        } else {
          setNewName({ value: "", error: true, errorText: "Enter name", success: false });
        }
        break;
      }
      case "newPhone": {
        if (isAutoPopulated) {
          if (value && value.length) {
            setNewPhone({ value, error: false, errorText: "Enter phone number", success: true });
          } else {
            setNewPhone({ value: "", error: true, errorText: "Enter phone number", success: false });
          }
        } else {
          const isIntlFormat = value.startsWith("+");
          if (isIntlFormat) {
            const rawDigits = value.replace(/[^0-9]/g, "");
            if (rawDigits.length === 0) {
              setNewPhone({ value: "+", error: true, errorText: "Enter +1 followed by 10 digits", success: false });
            } else {
              const forced = "1" + rawDigits.replace(/^1/, "");
              const cleaned = "+" + forced;
              const phoneDigits = forced.slice(1);
              if (phoneDigits.length === 10) {
                setNewPhone({ value: cleaned, error: false, errorText: "Enter phone number", success: true });
              } else if (phoneDigits.length > 10) {
                setNewPhone({ value: cleaned, error: true, errorText: "Only 10 digits allowed", success: false });
              } else {
                setNewPhone({ value: cleaned, error: true, errorText: "Enter +1 followed by 10 digits", success: false });
              }
            }
          } else {
            const digits = value.replace(/\D/g, "");
            const formatted = formatPhoneNumber(value);
            if (digits.length === 10) {
              setNewPhone({ value: formatted, error: false, errorText: "Enter phone number", success: true });
            } else if (digits.length > 10) {
              setNewPhone({ value: formatted, error: true, errorText: "Only 10 digits allowed", success: false });
            } else if (digits.length > 0) {
              setNewPhone({ value: formatted, error: true, errorText: "Enter 10 digit phone number", success: false });
            } else {
              setNewPhone({ value: "", error: true, errorText: "Enter phone number", success: false });
            }
          }
        }
        break;
      }
      case "newEmail": {
        if (value && value.length) {
          if (EMAIL_REGEX_C360.test(value)) {
            setNewEmail({ value, error: false, errorText: "Enter email", success: true });
          } else {
            setNewEmail({ value, error: true, errorText: "Enter a valid email", success: false });
          }
        } else {
          setNewEmail({ value: "", error: true, errorText: "Enter email", success: false });
        }
        break;
      }
    }
  };

  // ✅ Early return AFTER all hooks
  if (!isOpen) return null;

  const handleSave = () => {
    let contactObj = {
      contactName: newName.value?.trim(),
      phone: newPhone.value?.trim(),
      email: newEmail.value?.trim(),
    };
    onSave(
      {
        ...address,
      },
      contactObj
    );
    resetData();
  };
  const handleCancel = () => {
    resetData();
    onCancel();
  };

  const resetData = () => {
    setAddress(INITIAL_ADDRESS_STATE);
    setNewName({
      value: "",
      error: false,
      errorText: "Enter name",
      success: false,
    });
    setNewPhone({
      value: "",
      error: false,
      errorText: "Enter phone",
      success: false,
    });
    setNewEmail({
      value: "",
      error: false,
      errorText: "Enter email",
      success: false,
    });
  };
  return (
    <div className="install-modal-overlay">
      <div className="install-modal-container">
        {/* Close button */}
        <button className="install-modal-close-btn" onClick={handleCancel} aria-label="Close modal">
          <Icon name="close" size="small" color="#000" />
        </button>

        {/* Title */}
        <h3 className="install-modal-title">{label}</h3>
        {typeofModal === "shipping" && (
          <CommonValidateAddress
            address={address}
            setAddress={setAddress}
            showHistoricalAddressDropdown={true}
            setLoader={setLoader}
            variant="vehicle"
            showTitle={false}
            showTypedAddressOption={false}
            defaultCountryOnNew="USA"
            trackLocusFields={true}
            styles={{ containerWidth: "100%", radioGroupWidth: "100%" }}
            showShippingAddressLabel={false}
            bulkActionShippingPopup={true}
            modal={true}
          />
        )}

        {/* Contact Name & Phone - side by side */}
        <div className="install-modal-row">
          <div className="install-modal-field">
            <CustomInput
              width="100%"
              label="Contact Name*"
              aria-label="Contact Name"
              error={newName.error}
              errorText={newName.errorText}
              success={newName.success}
              value={newName.value}
              maxLength={50}
              css={{ paddingRight: "10px" }}
              onChange={(event) => handleFields("newName", event.target.value, "")}
            />
          </div>
          <div className="install-modal-field">
            <CustomInput
              width="100%"
              type="text"
              label="Phone*"
              aria-label="Phone"
              maxLength={16}
              error={newPhone.error}
              errorText={newPhone.errorText}
              success={newPhone.success}
              value={newPhone.value}
              onChange={(event) => handleFields("newPhone", event.target.value)}
            />
          </div>
        </div>

        {/* Email - full width */}
        <div className="install-modal-field-full">
          <CustomInput
            width="100%"
            type="text"
            label="Email*"
            aria-label="Email"
            error={newEmail.error}
            errorText={newEmail.errorText}
            success={newEmail.success}
            value={newEmail.value}
            onChange={(event) => handleFields("newEmail", event.target.value, "")}
          />
        </div>

        {/* Cancel & Save Buttons */}
        <div className="install-modal-actions">
          <button className="install-modal-cancel-btn" onClick={handleCancel}>
            Cancel
          </button>
          <button className="install-modal-save-btn" onClick={handleSave} disabled={isSaveDisabled}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

AddressModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  label: PropTypes.string,
  typeofModal: PropTypes.string,
};

export default AddressModal;
